/**
 * useGeoLocation.ts
 * ─────────────────────────────────────────────────────────────
 * Multi-signal, free-only country detection hook.
 *
 * Detection order (cascading):
 *   1. sessionStorage cache         → instant, zero latency
 *   2. Browser Geolocation API      → GPS accuracy, needs user permission
 *      └─ Reverse geocoded via Nominatim (OpenStreetMap, free, no key)
 *   3. Two free IP-geo APIs raced    → whichever responds first wins
 *      ├─ ipapi.co/json
 *      └─ ipwho.is
 *   4. Timezone heuristic           → last resort, never used to hard-block
 *   5. Fail open                    → unknown country, no banner shown
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { isCountryBlocked } from '../geoConfig';

const CACHE_KEY = 'armf_geo_country';

export interface GeoResult {
  countryCode: string | null;
  countryName: string | null;
  isBlocked: boolean;
  isLoading: boolean;
  detectionSource: 'cache' | 'gps' | 'ip' | 'timezone' | 'unknown';
}

// ── Timezone → Country mapping (heuristic, handles most unambiguous timezones) ──
const TIMEZONE_COUNTRY_MAP: Record<string, string> = {
  'Asia/Kabul': 'AF',
  'Asia/Pyongyang': 'KP',
  'Asia/Tehran': 'IR',
  'America/Havana': 'CU',
  'Asia/Damascus': 'SY',
  'Africa/Khartoum': 'SD',
  'Asia/Yangon': 'MM',
  'Africa/Tripoli': 'LY',
  'Africa/Mogadishu': 'SO',
  'Asia/Aden': 'YE',
};

function getTimezoneCountry(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_COUNTRY_MAP[tz] ?? null;
  } catch {
    return null;
  }
}

// ── Reverse geocode GPS coordinates via Nominatim (OpenStreetMap, free) ──
async function reverseGeocodeGPS(lat: number, lon: number): Promise<string | null> {
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=3`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const data = await resp.json();
    return (data?.address?.country_code as string)?.toUpperCase() ?? null;
  } catch {
    return null;
  }
}

// ── Get country from browser Geolocation + Nominatim ──
async function detectViaGPS(): Promise<string | null> {
  if (!navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const code = await reverseGeocodeGPS(pos.coords.latitude, pos.coords.longitude);
        resolve(code);
      },
      () => resolve(null),  // user denied or timed out
      { timeout: 6000, maximumAge: 3600000 }
    );
  });
}

// ── Race free IP-geo APIs; first valid response wins ──
async function detectViaIP(): Promise<{ code: string; name: string } | null> {
  const fetchers = [
    // ipapi.co — 1,000 req/day free
    fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) })
      .then((r) => r.json())
      .then((d) => {
        if (d?.country_code) return { code: d.country_code as string, name: d.country_name as string };
        return null;
      }),
    // ipwho.is — unlimited free, no key
    fetch('https://ipwho.is/', { signal: AbortSignal.timeout(5000) })
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && d?.country_code) return { code: d.country_code as string, name: d.country as string };
        return null;
      }),
  ];

  try {
    // Promise.any resolves with the first non-rejected result
    const result = await Promise.any(
      fetchers.map((p) => p.then((r) => { if (!r) throw new Error('null'); return r; }))
    );
    return result ?? null;
  } catch {
    return null;
  }
}

// ── Main hook ──
export function useGeoLocation(): GeoResult {
  const [result, setResult] = useState<GeoResult>({
    countryCode: null,
    countryName: null,
    isBlocked: false,
    isLoading: true,
    detectionSource: 'unknown',
  });

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      // 1. Check session cache first
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as { code: string; name: string };
          if (!cancelled) {
            setResult({
              countryCode: parsed.code,
              countryName: parsed.name,
              isBlocked: isCountryBlocked(parsed.code),
              isLoading: false,
              detectionSource: 'cache',
            });
          }
          return;
        } catch {
          sessionStorage.removeItem(CACHE_KEY);
        }
      }

      // 2. Try browser GPS (runs concurrently with IP detection for speed)
      const [gpsCode, ipData] = await Promise.all([
        detectViaGPS().catch(() => null),
        detectViaIP().catch(() => null),
      ]);

      let finalCode: string | null = null;
      let finalName: string | null = null;
      let source: GeoResult['detectionSource'] = 'unknown';

      if (gpsCode) {
        finalCode = gpsCode.toUpperCase();
        finalName = ipData?.code?.toUpperCase() === finalCode ? (ipData?.name ?? finalCode) : finalCode;
        source = 'gps';
      } else if (ipData) {
        finalCode = ipData.code.toUpperCase();
        finalName = ipData.name;
        source = 'ip';
      } else {
        // 4. Timezone heuristic as last resort
        const tzCode = getTimezoneCountry();
        if (tzCode) {
          finalCode = tzCode;
          finalName = tzCode;
          source = 'timezone';
        }
      }

      if (finalCode) {
        // Cache result for this session
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ code: finalCode, name: finalName ?? finalCode }));
      }

      if (!cancelled) {
        setResult({
          countryCode: finalCode,
          countryName: finalName,
          isBlocked: finalCode ? isCountryBlocked(finalCode) : false,
          isLoading: false,
          detectionSource: source,
        });
      }
    }

    detect();
    return () => { cancelled = true; };
  }, []);

  return result;
}
